import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fmtDate } from '@hrms/shared';
import { api } from '../../../lib/api';
import { humanize, todayISO } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { useGo } from '../../../navigation/nav';
import { Button, Card, DataTable, DateField, Dot, Loading, Modal, PageHeader, Row, Screen, Select, Text, TextField, useTheme } from '../../../ui';
import { BalanceCards } from '../components/BalanceCards';

export function LeaveBalanceScreen() {
  const go = useGo();
  const { colors } = useTheme();
  const balances = useQuery({ queryKey: ['leave', 'balances', 'me'], queryFn: () => api.leave.balances() });
  const [type, setType] = useState<number | 0>(0);
  const ledger = useQuery({ queryKey: ['leave', 'ledger', 'me', type], queryFn: () => api.leave.ledger({ leave_type_id: type || undefined }) });
  const [open, setOpen] = useState(false);
  const [co, setCo] = useState({ worked_date: todayISO(), reason: '' });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const comp = useMut(() => api.leave.compOff(co), {
    invalidate: [['leave']],
    success: 'Comp-off request submitted',
    onSuccess: () => {
      setOpen(false);
      setCo({ worked_date: todayISO(), reason: '' });
    },
    onError: (e) => setErrs(fieldErrors(e)),
  });
  const typeName = new Map((balances.data ?? []).map((b) => [b.leave_type_id, b.leave_type]));
  return (
    <Screen refreshing={balances.isRefetching} onRefresh={() => { balances.refetch(); ledger.refetch(); }}>
      <PageHeader
        title="Leave"
        actions={
          <>
            <Button variant="primary" icon="leaf" title="Apply leave" onPress={() => go('ApplyLeave')} />
            <Button icon="plus" title="Request comp-off" onPress={() => setOpen(true)} />
            <Button icon="calendar" variant="ghost" title="Team calendar" onPress={() => go('TeamLeaveCalendar')} />
          </>
        }
      />
      {balances.isLoading ? <Loading /> : <BalanceCards balances={balances.data ?? []} />}
      <Card
        title="Leave ledger"
        actions={
          <Select
            value={type}
            onChange={setType}
            options={[{ value: 0, label: 'All types' }, ...(balances.data ?? []).map((b) => ({ value: b.leave_type_id, label: b.leave_type.name }))]}
          />
        }
      >
        <DataTable
          rows={ledger.data}
          loading={ledger.isLoading}
          emptyTitle="No ledger entries"
          columns={[
            { key: 'date', title: 'Date', width: 110, render: (r) => fmtDate(r.date), sortValue: (r) => r.date },
            {
              key: 'leave_type_id',
              title: 'Type',
              width: 80,
              sortValue: (r) => typeName.get(r.leave_type_id)?.code,
              render: (r) => (
                <Row gap={6}>
                  <Dot color={typeName.get(r.leave_type_id)?.color ?? colors.faint} />
                  <Text>{typeName.get(r.leave_type_id)?.code ?? r.leave_type_id}</Text>
                </Row>
              ),
            },
            { key: 'reason', title: 'Transaction', width: 130, render: (r) => humanize(r.reason), sortValue: (r) => r.reason },
            { key: 'delta', title: 'Days', width: 70, align: 'right', sortValue: (r) => r.delta, render: (r) => <Text weight="semibold" color={r.delta < 0 ? colors.danger : colors.success}>{r.delta > 0 ? `+${r.delta}` : String(r.delta)}</Text> },
            { key: 'note', title: 'Note', width: 180, flex: 2 },
          ]}
        />
      </Card>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Request compensatory off"
        width={440}
        footer={
          <>
            <Button title="Cancel" onPress={() => setOpen(false)} />
            <Button title="Submit" variant="primary" disabled={!co.reason.trim()} loading={comp.isPending} onPress={() => comp.mutate(undefined)} />
          </>
        }
      >
        <DateField label="Date worked (holiday / weekly off)" required max={todayISO()} value={co.worked_date} onChange={(worked_date) => setCo({ ...co, worked_date })} error={errs.worked_date} />
        <TextField label="Reason" required multiline value={co.reason} onChangeText={(reason) => setCo({ ...co, reason })} error={errs.reason} />
      </Modal>
    </Screen>
  );
}
